import loadUserback, { type UserbackOptions, type UserbackWidgetSettings } from '@userback/widget';

import { installUserbackZhTwPatch } from './userback-zh-tw-patch';

/**
 * Userback feedback glue for the bring-your-own-button setup.
 *
 * The widget is NOT loaded at startup. `@userback/widget` is only a loader for
 * Userback's remote script, and we fetch it the first time the user reaches for
 * "Send feedback" in the AppNav — hovering or focusing it (`preload`), or
 * picking it (`open`). So no third-party code runs, and nothing is sent to
 * Userback, until the user goes for the feedback item specifically. That's also
 * why this is not tied to the cookie-consent categories: the user's own action
 * is the opt-in — which is why the warm-up hangs off that item and not off the
 * menu opening, since people open that menu to navigate.
 *
 * The launcher button is hidden (`autohide`) because Userback docks it
 * bottom-right, where the editors already place the inspector reopen pill and
 * command-key toggle. The AppNav item opens the form instead.
 *
 * Everything takes the loader as an injectable parameter so tests can pass a
 * fake instead of `mock.module`-ing `@userback/widget` — that mock is
 * process-global and leaks across test files.
 */

/** The subset of the Userback widget this module drives. */
export interface UserbackWidgetLike {
    open: () => void;
    destroy: () => void;
    isLoaded: () => boolean;
}

/** How long to wait for Userback's `on_load` before giving up on an open. */
const DEFAULT_LOAD_TIMEOUT_MS = 30_000;

export type UserbackLoader = (token: string, options?: UserbackOptions) => Promise<UserbackWidgetLike>;

export type UserbackLanguage = NonNullable<UserbackWidgetSettings['language']>;

/** App locale → Userback widget language. Userback spells Japanese "jp". */
const USERBACK_LANGUAGES: Record<string, UserbackLanguage> = {
    en: 'en',
    ja: 'jp',
    'zh-TW': 'zh-TW',
};

/**
 * Map an i18next language tag to Userback's language code. Tries the exact tag
 * first, then the base language (`en-US` → `en`). Undefined means "let the
 * widget use its configured default".
 */
export function userbackLanguage(lng: string): UserbackLanguage | undefined {
    return USERBACK_LANGUAGES[lng] ?? USERBACK_LANGUAGES[lng.split('-')[0] ?? ''];
}

/** Resolve the Userback project token from the environment (blank → unset). */
export function resolveUserbackToken(
    env: { VITE_USERBACK_TOKEN?: string } = import.meta.env,
): string | undefined {
    const token = env.VITE_USERBACK_TOKEN?.trim();
    return token ? token : undefined;
}

/** Static metadata attached to every submission, shown on the feedback item
 *  in Userback and filterable there. Userback has no environment concept of
 *  its own (one workspace token, projects routed by domain), so this is how
 *  dev / staging / production feedback is told apart. */
export interface UserbackCustomData {
    environment: string;
    release?: string;
}

/** Resolve the environment tags. Mirrors Sentry's `environment` resolution in
 *  `instrument.ts`: prefer the CI-injected deploy env, else Vite's build mode. */
export function resolveUserbackCustomData(
    env: { VITE_DEPLOY_ENV?: string; MODE: string; VITE_APP_VERSION?: string } = import.meta.env,
): UserbackCustomData {
    const data: UserbackCustomData = { environment: env.VITE_DEPLOY_ENV?.trim() || env.MODE };
    const release = env.VITE_APP_VERSION?.trim();
    if (release) data.release = release;
    return data;
}

export interface FeedbackOpener {
    /** Whether feedback can be opened at all (a token is configured). */
    isAvailable: () => boolean;
    /**
     * Open the feedback form in the given app language, loading the widget on
     * first use. Resolves to `false` (without side effects) when no token is
     * configured, or when the remote widget failed to load — a later call
     * retries the load.
     */
    open: (language: string) => Promise<boolean>;
    /**
     * Warm the widget for the given language without opening the form, so a
     * later {@link open} is instant. Fetching Userback's remote script and
     * waiting out its page-load delay is the whole cost of a first open, and
     * it's several seconds on a cold cache — call this on intent (the AppNav
     * menu opening) rather than on the click itself. Same resolution contract
     * as `open`; a failed preload is not cached, so the click retries.
     */
    preload: (language: string) => Promise<boolean>;
}

interface FeedbackOpenerDeps {
    token: string | undefined;
    load: UserbackLoader;
    /** Attached as `custom_data` on init; omitted when undefined. */
    customData?: UserbackCustomData;
    /** Test hook; defaults to {@link DEFAULT_LOAD_TIMEOUT_MS}. */
    loadTimeoutMs?: number;
    /**
     * Per-language DOM patch applied once the widget is loaded; returns its
     * uninstall function. Defaults to the zh-TW wording fix
     * (`userback-zh-tw-patch.ts`); nothing for other languages.
     */
    localePatch?: (language: UserbackLanguage | undefined) => (() => void) | undefined;
}

/** The default locale patch: only Userback's zh-TW table needs fixing. */
export function defaultLocalePatch(language: UserbackLanguage | undefined): (() => void) | undefined {
    return language === 'zh-TW' ? installUserbackZhTwPatch() : undefined;
}

export function createFeedbackOpener({
    token,
    load,
    customData,
    loadTimeoutMs = DEFAULT_LOAD_TIMEOUT_MS,
    localePatch = defaultLocalePatch,
}: FeedbackOpenerDeps): FeedbackOpener {
    let loaded: { language: UserbackLanguage | undefined; widget: Promise<UserbackWidgetLike> } | undefined;
    let uninstallPatch: (() => void) | undefined;

    /**
     * Load the widget and resolve only once it can actually open a form.
     *
     * The npm loader resolves on Userback's `on_init`, which the widget fires
     * as soon as its config response arrives — BEFORE it has loaded the widget
     * code (`isLoaded()` is still false, and `open()` just warns "Widget code
     * not loaded"). Readiness is signalled later via `on_load` (after the
     * project's page-load delay), so we wait for that too.
     */
    const loadWidget = (language: UserbackLanguage | undefined): Promise<UserbackWidgetLike> =>
        new Promise<UserbackWidgetLike>((resolve, reject) => {
            let widget: UserbackWidgetLike | undefined;
            let ready = false;
            const timer = setTimeout(
                () => reject(new Error(`Userback widget did not finish loading within ${loadTimeoutMs}ms`)),
                loadTimeoutMs,
            );
            const settle = () => {
                if (!widget || !ready) return;
                clearTimeout(timer);
                resolve(widget);
            };
            const options: UserbackOptions = {
                autohide: true,
                on_load: () => { ready = true; settle(); },
            };
            if (language) options.widget_settings = { language };
            if (customData) options.custom_data = customData;
            load(token!, options).then(
                (w) => {
                    widget = w;
                    if (w.isLoaded()) ready = true;
                    settle();
                },
                (error: unknown) => { clearTimeout(timer); reject(error); },
            );
        });

    /**
     * Resolve a ready-to-open widget for `language`, loading it on first use
     * and reusing it afterwards. Rejects (without caching the rejection) when
     * the load fails or times out.
     */
    const ensureWidget = async (language: UserbackLanguage | undefined): Promise<UserbackWidgetLike> => {
        if (loaded && loaded.language !== language) {
            // The widget reads its language once at init; rebuild it so the
            // form follows the live i18next language.
            const stale = loaded;
            loaded = undefined;
            uninstallPatch?.();
            uninstallPatch = undefined;
            await stale.widget.then((w) => w.destroy(), () => undefined);
        }
        if (!loaded) loaded = { language, widget: loadWidget(language) };
        const current = loaded;
        try {
            const widget = await current.widget;
            // Install once per widget instance, only after it actually loaded.
            if (loaded === current && !uninstallPatch) uninstallPatch = localePatch(language);
            return widget;
        } catch (error) {
            // Don't cache the rejection — the next click should retry.
            if (loaded === current) loaded = undefined;
            throw error;
        }
    };

    const warn = (error: unknown) => {
        if (import.meta.env.DEV) console.warn('[userback] failed to load widget', error);
    };

    return {
        isAvailable: () => token !== undefined,
        async open(appLanguage) {
            if (token === undefined) return false;
            try {
                (await ensureWidget(userbackLanguage(appLanguage))).open();
                return true;
            } catch (error) {
                warn(error);
                return false;
            }
        },
        async preload(appLanguage) {
            if (token === undefined) return false;
            try {
                await ensureWidget(userbackLanguage(appLanguage));
                return true;
            } catch (error) {
                warn(error);
                return false;
            }
        },
    };
}

/** The app-wide opener, backed by the real loader and `VITE_USERBACK_TOKEN`. */
export const feedbackOpener: FeedbackOpener = createFeedbackOpener({
    token: resolveUserbackToken(),
    load: loadUserback,
    customData: resolveUserbackCustomData(),
});
